import * as React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import Summary from 'features/user-profile/components/Summary';
import { Theme } from 'helpers/theme';
import Spacer from 'sharedComponents/core/Spacers/Spacer';
import { commitMutation, createFragmentContainer, graphql } from 'react-relay';
import { Router } from 'found';
import { DelegateProfilePage_delegate } from './__generated__/DelegateProfilePage_delegate.graphql';
import moment from 'moment';
import { Page } from 'sharedComponents/Pagination/Paginator/Pageinator';
import Button from 'components/Button/Button';
import environment from 'api/environment';
import { GraphError } from 'types/general';
import { DelegateProfilePage_RequestBackgroundCheckMutationResponse } from './__generated__/DelegateProfilePage_RequestBackgroundCheckMutation.graphql';
import { useToast } from 'components/Toasts/ToastProvider';

import { DelegateProfilePage_manager } from './__generated__/DelegateProfilePage_manager.graphql';

import CourseTable from 'features/user-profile/components/CourseTable';
import DocumentsTable from 'features/user-profile/components/DocumentsTable';
import ProfileCard from 'features/user-profile/components/ProfileCard';
import SimulatorCard from 'features/user-profile/components/SimulatorCard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import ActivityTable from 'features/user-profile/components/ActivityTable';
import SingleUser from 'components/SingleUser';
import { CheckType } from 'features/background-checks/components/RequestBackgroundCheckModal/RequestBackgroundCheckModal';

const useStyles = createUseStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    padding: '25px 25px'
  },
  top: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  divider: {
    width: theme.spacing(1)
  },
  quickOverview: {
    gridArea: 'overvw'
  },
  trainingProgress: {
    gridArea: 'traini'
  },
  cardFlex: {
    display: 'flex'
  },
  grid: {
    marginTop: 19,
    display: 'flex',
    gap: '1rem'
  },
  grid__right: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  headerActions: {
    display: 'flex',
    gap: '16px',
    alignItems: 'center'
  },
  noCourses: {
    textAlign: 'center',
    fontWeight: 300,
    color: theme.colors.textGrey
  },
  courseName: {
    fontWeight: 700,
    fontSize: '0.9rem'
  },
  hasActive: {
    gap: '12px',
    color: '#18c329',
    border: '1px solid #18c329',
    display: 'flex',
    padding: '9px 15px',
    fontSize: 12,
    background: 'white',
    alignItems: 'center',
    fontWeight: 500,
    borderRadius: 24,
    height: 32
  },
  back: {}
}));

const mutation = graphql`
  mutation DelegateProfilePage_RequestBackgroundCheckMutation(
    $delegateUUID: UUID!
    $email: String
    $requestDBS: Boolean!
    $isRenewal: Boolean
    $onlyDBS: Boolean
    $idExpiry: Time
    $isCAA: Boolean
  ) {
    requestBackgroundCheck(
      delegateUUID: $delegateUUID
      requestDBS: $requestDBS
      email: $email
      isRenewal: $isRenewal
      onlyDBS: $onlyDBS
      idExpiryDate: $idExpiry
      isCAA: $isCAA
    )
  }
`;

type Props = {
  delegate: DelegateProfilePage_delegate;
  manager: DelegateProfilePage_manager;
  retry: () => void;
  router: Router;
};

const DelegateProfilePage = ({ delegate, manager, router, retry }: Props) => {
  const theme = useTheme();
  const classes = useStyles({ theme });
  const [isOpen, setIsOpen] = React.useState(false);

  const toast = useToast();

  const requestBackgroundCheck = (
    requestDBS: Boolean,
    checkType: CheckType,
    idExpiry?: moment.Moment
  ) => {
    const variables = {
      delegateUUID: delegate.uuid,
      requestDBS: requestDBS,
      isRenewal: checkType === 'renewal',
      onlyDBS: checkType === 'onlyDBS',
      idExpiry: idExpiry,
      isCAA: checkType === 'onlyDBS' ? false : true
    };

    commitMutation(environment, {
      mutation,
      variables,
      onCompleted: async (
        response: DelegateProfilePage_RequestBackgroundCheckMutationResponse,
        errors: GraphError[]
      ) => {
        if (errors) {
          toast({
            message: {
              variant: 'error',
              text: errors?.[0]?.extensions?.helpText ?? errors?.[0]?.message
            }
          });
          console.error(errors);
          return;
        }

        if (response.requestBackgroundCheck) {
          toast({
            message: {
              variant: 'success',
              text: 'Background check requested'
            }
          });
          retry();
        }
      },
      onError: (err) => {
        console.log(err);
        toast({
          message: {
            variant: 'error',
            text:
              err?.[0]?.extensions?.helpText ?? err?.[0]?.message ?? err.message
          }
        });
      }
    });
  };

  // Summary variables
  const hasLoggedIn = () =>
    moment(delegate.lastLogin).isAfter(moment('0001-01-01T00:00:00Z'));

  const numOfActiveCourses =
    (delegate.myCourses?.filter((x) => x.status === 'incomplete').length ?? 0) +
    (delegate.myClassroomCourses?.filter((x) => x.status === 'incomplete')
      .length ?? 0);

  const numLastActive = hasLoggedIn()
    ? moment().diff(moment(delegate.lastLogin), 'days')
    : -1;

  const numOfCertificates =
    (delegate.myCourses?.filter((x) => x.certificateURL !== null).length ?? 0) +
    (delegate.myClassroomCourses?.filter((x) => x.certificateURL !== null)
      .length ?? 0);

  const numOfExpiringSoon =
    (delegate.myCourses?.filter(
      (x) =>
        x.expiresAt &&
        moment(x.expiresAt).diff(moment(), 'days') < 28 &&
        moment(x.expiresAt).isAfter(new Date())
    ).length ?? 0) +
    (delegate.myClassroomCourses?.filter(
      (x) =>
        x.expiresAt &&
        moment(x.expiresAt).diff(moment(), 'days') < 28 &&
        moment(x.expiresAt).isAfter(new Date())
    ).length ?? 0) +
    (delegate.documents?.edges?.filter((x) => {
      if (x !== null && x.expiryDate) {
        return (
          x.expiryDate &&
          moment(x.expiryDate).diff(moment(), 'days') < 28 &&
          moment(x.expiryDate).isAfter(new Date())
        );
      }
      return false;
    }).length ?? 0);

  return (
    <div className={classes.root}>
      <div className={classes.top}>
        <div className={classes.back}>
          <Button archetype="flat" onClick={() => router.go(-1)}>
            <FontAwesomeIcon icon={faChevronLeft} /> Back to delegates
          </Button>
        </div>
      </div>
      <div className={classes.grid}>
        <ProfileCard
          delegate={delegate}
          manager={manager}
          onRequestBackgroundCheck={requestBackgroundCheck}
        />
        <div className={classes.grid__right}>
          <Summary
            numActiveCourses={numOfActiveCourses}
            numLastActive={numLastActive}
            numCertificates={numOfCertificates}
            numExpiringSoon={numOfExpiringSoon}
          />
          {delegate.hasSimulatorAccess && (
            <SimulatorCard delegateUUID={delegate.uuid} />
          )}
        </div>
      </div>

      <Spacer spacing={3} vertical />
      <CourseTable delegate={delegate} />
      <Spacer vertical spacing={3} />
      <DocumentsTable delegate={delegate} />
      <Spacer vertical spacing={3} />
      <ActivityTable delegateUUID={delegate.uuid} />
      <SingleUser
        user={{ firstName: delegate.firstName, uuid: delegate.uuid }}
        isOpen={isOpen}
        onClose={() => setIsOpen((previous) => !previous)}
      />
    </div>
  );
};

const DelegateProfilePageFrag = createFragmentContainer(DelegateProfilePage, {
  delegate: graphql`
    fragment DelegateProfilePage_delegate on Delegate {
      uuid
      firstName
      lastName
      email
      jobTitle
      telephone
      TTC_ID
      lastLogin
      currentBackgroundCheck {
        idExpiryDate
        dbsExpiryDate
      }
      hasActiveBackgroundCheck
      backgroundCheckPending
      company {
        name
      }
      hasSimulatorAccess
      myCourses {
        status
        expiresAt
        enrolledAt
        certificateURL
        progress {
          total
          completed
        }
        partTwo {
          status
          certificateURL
          expiresAt
        }
        course {
          name
          category {
            name
          }
          ... on OnlineCourse {
            partTwo {
              ident: id
            }
          }
        }
      }
      myClassroomCourses {
        status
        expiresAt
        enrolledAt
        certificateURL
        course {
          ident: id
          name
          category {
            name
          }
        }
      }
      ...CourseTable_delegate
      ...DocumentsTable_delegate
      ...ProfileCard_delegate
      documents {
        edges {
          uuid
          name
          expiryDate
        }
      }
    }
  `,
  manager: graphql`
    fragment DelegateProfilePage_manager on Manager {
      ...ProfileCard_manager
    }
  `
});

export default DelegateProfilePageFrag;
